import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function AdminManagement() {
  const { token, admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';

  const fetchAdmins = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      } else {
        setError('Failed to load admin list');
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load admin list');
    }
  };

  useEffect(() => {
    if (token) {
      fetchAdmins();
    }
  }, [token]);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Admin "${newUsername}" created successfully!`);
        setNewUsername('');
        setNewPassword('');
        fetchAdmins(); // Refresh the list
      } else {
        setError(data.error || 'Failed to create admin');
      }
    } catch (err) {
      console.error('Error creating admin:', err);
      setError('Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiUrl}/admin/${username}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Admin "${username}" deleted successfully!`);
        fetchAdmins(); // Refresh the list
      } else {
        setError(data.error || 'Failed to delete admin');
      }
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError('Failed to delete admin');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-3xl font-bold mb-6">Admin Management</h2>

      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Create New Admin Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-xl font-bold mb-4">Create New Admin</h3>
        <form onSubmit={handleCreateAdmin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Username</label>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter username"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter password (min 8 characters)"
              minLength="8"
              required
            />
            <p className="text-sm text-gray-600 mt-1">
              Password must be at least 8 characters long
            </p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Admin'}
          </button>
        </form>
      </div>

      {/* Admin List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">Current Admins</h3>
        {admins.length === 0 ? (
          <p className="text-gray-600">Loading admins...</p>
        ) : (
          <div className="space-y-4">
            {admins.map((admin) => (
              <div
                key={admin.username}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
              >
                <div>
                  <p className="font-semibold">{admin.username}</p>
                  <p className="text-sm text-gray-600">
                    Created: {new Date(admin.createdAt).toLocaleDateString()}
                    {admin.createdBy && ` by ${admin.createdBy}`}
                  </p>
                  {currentAdmin && currentAdmin.username === admin.username && (
                    <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                      You
                    </span>
                  )}
                </div>
                {currentAdmin && currentAdmin.username !== admin.username && (
                  <button
                    onClick={() => handleDeleteAdmin(admin.username)}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminManagement;
